import React, { useState, useEffect } from 'react';
import { getSubmissions, deleteSubmission } from '../lib/supabase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import AnalyticsTab from './AnalyticsTab';

const {
  FiRefreshCw, FiSearch, FiChevronDown, FiChevronUp, FiMail, FiBriefcase,
  FiTag, FiUser, FiLock, FiLogOut, FiFilter, FiClock, FiBarChart2,
  FiList, FiTrash2, FiAlertTriangle
} = FiIcons;

const AdminDashboard = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'completed', 'partial'
  const [activeTab, setActiveTab] = useState('submissions'); // 'submissions', 'analytics'
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  const correctPassword = 'Afc*123best!';

  const attemptLogin = () => {
    if (password === correctPassword) {
      setIsAuthenticated(true);
      setAuthError(null);
      // Store authentication in session storage so it persists during the session
      sessionStorage.setItem('adminAuthenticated', 'true');
      loadSubmissions();
    } else {
      setAuthError('Incorrect password. Please try again.');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('adminAuthenticated');
  };

  const loadSubmissions = async () => {
    setLoading(true);
    setError(null);
    try {
      const { success, data, error } = await getSubmissions();
      if (success && data) {
        setSubmissions(data);
      } else {
        setError(error?.message || 'Failed to load submissions');
      }
    } catch (err) {
      setError('An unexpected error occurred');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check if already authenticated in this session
    const authenticated = sessionStorage.getItem('adminAuthenticated') === 'true';
    if (authenticated) {
      setIsAuthenticated(true);
      loadSubmissions();
    } else {
      setLoading(false);
    }
  }, []);

  const toggleExpand = (id) => {
    if (expandedId === id) {
      setExpandedId(null);
    } else {
      setExpandedId(id);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const filterSubmissions = (submissions) => {
    if (filter === 'completed') {
      return submissions.filter(submission => !submission.isPartial);
    } else if (filter === 'partial') {
      return submissions.filter(submission => submission.isPartial);
    }
    return submissions;
  };

  const filteredSubmissions = filterSubmissions(
    submissions.filter(submission =>
      submission.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      submission.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      submission.company?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const handleDeleteClick = (id) => {
    setDeleteConfirmId(id);
    setDeleteError(null);
    setDeleteSuccess(false);
  };

  const confirmDelete = async (id) => {
    setDeleteLoading(true);
    setDeleteError(null);
    setDeleteSuccess(false);

    try {
      const { success, error } = await deleteSubmission(id);
      if (success) {
        setDeleteSuccess(true);
        // Remove the deleted submission from state
        setSubmissions(prev => prev.filter(s => s.id !== id));
        // Close the confirmation dialog after a brief delay
        setTimeout(() => {
          setDeleteConfirmId(null);
          setDeleteSuccess(false);
        }, 1500);
      } else {
        setDeleteError(error?.message || 'Failed to delete submission');
      }
    } catch (err) {
      setDeleteError('An unexpected error occurred while deleting');
      console.error(err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmId(null);
    setDeleteError(null);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-dark-700 flex items-center justify-center px-4">
        <div className="bg-dark-600 rounded-lg p-8 max-w-md w-full shadow-lg">
          <div className="flex justify-center mb-6">
            <div className="bg-dark-500 p-4 rounded-full">
              <SafeIcon icon={FiLock} className="w-8 h-8 text-primary-600" />
            </div>
          </div>

          <h1 className="text-2xl font-bold text-white text-center mb-6">Admin Authentication</h1>

          <div className="space-y-6">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && attemptLogin()}
                className="w-full px-4 py-3 bg-dark-400 border border-dark-300 text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Enter admin password"
              />
            </div>

            {authError && (
              <div className="text-danger-500 text-sm bg-danger-600 bg-opacity-20 p-3 rounded-md">
                {authError}
              </div>
            )}

            <button
              onClick={attemptLogin}
              className="w-full bg-primary-600 text-white font-semibold py-3 rounded-lg hover:bg-primary-700 transition-colors"
            >
              Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-700 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <h1 className="text-3xl font-bold text-white">Brand Health Admin</h1>
          <div className="flex gap-3">
            <button
              onClick={loadSubmissions}
              className="flex items-center px-4 py-2 bg-dark-500 text-white rounded-lg hover:bg-dark-400 transition-colors"
              disabled={loading}
            >
              <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center px-4 py-2 bg-dark-500 text-white rounded-lg hover:bg-dark-400 transition-colors"
            >
              <SafeIcon icon={FiLogOut} className="w-4 h-4 mr-2" />
              Logout
            </button>
          </div>
        </div>

        <div className="mb-6">
          <div className="flex mb-4 border-b border-dark-400 overflow-x-auto">
            <button
              onClick={() => setActiveTab('submissions')}
              className={`py-3 px-5 font-medium text-base flex items-center whitespace-nowrap ${
                activeTab === 'submissions'
                  ? 'text-primary-600 border-b-2 border-primary-600'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              <SafeIcon icon={FiList} className="w-5 h-5 mr-2" />
              Submissions
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`py-3 px-5 font-medium text-base flex items-center whitespace-nowrap ${
                activeTab === 'analytics'
                  ? 'text-primary-600 border-b-2 border-primary-600'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              <SafeIcon icon={FiBarChart2} className="w-5 h-5 mr-2" />
              Analytics
            </button>
          </div>
        </div>

        {activeTab === 'submissions' ? (
          <>
            <div className="mb-6 space-y-4">
              <div className="relative">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, email or company..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-dark-500 text-white rounded-lg border border-dark-400 focus:border-primary-600 focus:outline-none"
                />
              </div>

              <div className="flex flex-wrap gap-2">
                <div className="flex items-center">
                  <SafeIcon icon={FiFilter} className="mr-2 text-gray-400" />
                  <span className="text-gray-300 mr-3">Filter:</span>
                </div>
                <button
                  onClick={() => setFilter('all')}
                  className={`px-3 py-1 rounded-full text-sm ${
                    filter === 'all' ? 'bg-primary-600 text-white' : 'bg-dark-500 text-gray-300 hover:bg-dark-400'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setFilter('completed')}
                  className={`px-3 py-1 rounded-full text-sm ${
                    filter === 'completed' ? 'bg-success-600 text-white' : 'bg-dark-500 text-gray-300 hover:bg-dark-400'
                  }`}
                >
                  Completed
                </button>
                <button
                  onClick={() => setFilter('partial')}
                  className={`px-3 py-1 rounded-full text-sm ${
                    filter === 'partial' ? 'bg-warning-600 text-white' : 'bg-dark-500 text-gray-300 hover:bg-dark-400'
                  }`}
                >
                  Partial Completions
                </button>
              </div>

              <div className="bg-dark-600 text-gray-300 p-3 rounded-lg text-sm">
                <span className="font-semibold">Total:</span> {submissions.length} submissions |{' '}
                <span className="font-semibold ml-2">Completed:</span>{' '}
                {submissions.filter(s => !s.isPartial).length} |{' '}
                <span className="font-semibold ml-2">Partial:</span>{' '}
                {submissions.filter(s => s.isPartial).length}
              </div>
            </div>

            {error && (
              <div className="bg-danger-600 text-white p-4 rounded-lg mb-6">{error}</div>
            )}

            {loading ? (
              <div className="flex justify-center items-center h-64">
                <SafeIcon icon={FiRefreshCw} className="w-8 h-8 text-primary-600 animate-spin" />
              </div>
            ) : filteredSubmissions.length === 0 ? (
              <div className="text-center py-12 bg-dark-600 rounded-lg">
                <p className="text-gray-300 text-lg">
                  {searchTerm ? 'No submissions match your search.' : 'No submissions yet.'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredSubmissions.map(submission => (
                  <div
                    key={submission.id}
                    className={`bg-dark-600 rounded-lg overflow-hidden border ${
                      submission.isPartial ? 'border-warning-600' : 'border-dark-500'
                    }`}
                  >
                    <div className="p-4 flex flex-wrap justify-between items-center">
                      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => toggleExpand(submission.id)}>
                        <div className="flex items-center">
                          <SafeIcon icon={FiUser} className="w-4 h-4 text-gray-400 mr-2" />
                          <h3 className="text-lg font-semibold text-white truncate">{submission.name}</h3>
                        </div>
                        <div className="flex items-center mt-1">
                          <SafeIcon icon={FiMail} className="w-4 h-4 text-gray-400 mr-2" />
                          <p className="text-sm text-gray-300 truncate">{submission.email}</p>
                        </div>
                        {submission.isPartial && submission.progress && (
                          <div className="flex items-center mt-1">
                            <SafeIcon icon={FiClock} className="w-4 h-4 text-warning-500 mr-2" />
                            <p className="text-sm text-warning-500">Progress: {submission.progress}</p>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-3 mt-2 sm:mt-0">
                        <div
                          className={`px-3 py-1 rounded-full text-sm font-medium ${
                            submission.isPartial
                              ? 'bg-warning-600 bg-opacity-20 text-warning-500'
                              : submission.tier === 'Industry Leader'
                              ? 'bg-success-600 bg-opacity-20 text-success-500'
                              : submission.tier === 'Optimisation Opportunities'
                              ? 'bg-primary-600 bg-opacity-20 text-primary-500'
                              : submission.tier === 'Ready to Scale'
                              ? 'bg-warning-600 bg-opacity-20 text-warning-500'
                              : 'bg-danger-600 bg-opacity-20 text-danger-500'
                          }`}
                        >
                          {submission.isPartial ? 'Partial' : submission.tier}
                        </div>
                        {!submission.isPartial && (
                          <div className="text-lg font-bold text-primary-600">{submission.overall_score}</div>
                        )}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleDeleteClick(submission.id)}
                            className="p-1.5 rounded-full hover:bg-dark-400 text-danger-500 transition-colors"
                            title="Delete submission"
                          >
                            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => toggleExpand(submission.id)}
                            className="p-1.5 rounded-full hover:bg-dark-400 text-gray-400 transition-colors"
                            title={expandedId === submission.id ? "Collapse details" : "Expand details"}
                          >
                            <SafeIcon icon={expandedId === submission.id ? FiChevronUp : FiChevronDown} className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {expandedId === submission.id && (
                      <div className="border-t border-dark-500 p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <h4 className="text-sm font-medium text-gray-400 mb-2">Submission Date</h4>
                            <p className="text-white">{formatDate(submission.created_at)}</p>
                          </div>
                          {submission.company && submission.company !== 'Not provided' && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-400 mb-2">
                                <SafeIcon icon={FiBriefcase} className="w-4 h-4 inline mr-1" />
                                Company
                              </h4>
                              <p className="text-white">{submission.company}</p>
                            </div>
                          )}
                          {submission.role && submission.role !== 'Not provided' && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-400 mb-2">
                                <SafeIcon icon={FiTag} className="w-4 h-4 inline mr-1" />
                                Role
                              </h4>
                              <p className="text-white">{submission.role}</p>
                            </div>
                          )}
                          {submission.isPartial && submission.exit_point && (
                            <div>
                              <h4 className="text-sm font-medium text-warning-400 mb-2">
                                <SafeIcon icon={FiClock} className="w-4 h-4 inline mr-1" />
                                Exit Point
                              </h4>
                              <p className="text-warning-300">{submission.exit_point}</p>
                            </div>
                          )}
                        </div>

                        {!submission.isPartial ? (
                          <>
                            <div className="mb-4">
                              <h4 className="text-sm font-medium text-gray-400 mb-2">Category Scores</h4>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div className="bg-dark-500 p-3 rounded-lg">
                                  <div className="text-xs text-gray-400">Brand Positioning</div>
                                  <div className="text-lg font-semibold text-white">
                                    {submission.brand_strategy_score}/6
                                  </div>
                                </div>
                                <div className="bg-dark-500 p-3 rounded-lg">
                                  <div className="text-xs text-gray-400">Website Performance</div>
                                  <div className="text-lg font-semibold text-white">{submission.website_ux_score}/8</div>
                                </div>
                                <div className="bg-dark-500 p-3 rounded-lg">
                                  <div className="text-xs text-gray-400">Marketing System</div>
                                  <div className="text-lg font-semibold text-white">{submission.marketing_score}/6</div>
                                </div>
                              </div>
                            </div>

                            <div>
                              <h4 className="text-sm font-medium text-gray-400 mb-3">Detailed Responses</h4>
                              <div className="space-y-4">
                                {submission.responses && submission.responses.detailed && Object.entries(submission.responses.detailed).map(([category, answers]) => (
                                  <div key={category} className="bg-dark-700 rounded-lg p-3">
                                    <h5 className="font-medium text-white mb-2">{category}</h5>
                                    <div className="space-y-2">
                                      {answers.map((item, index) => (
                                        <div key={index} className="bg-dark-800 p-2 rounded">
                                          <p className="text-sm text-gray-300">{item.question}</p>
                                          <div className="flex justify-between items-center mt-1">
                                            <p className="text-sm text-white">{item.answer}</p>
                                            <span className="bg-dark-600 text-primary-500 px-2 py-0.5 rounded text-xs">
                                              {item.score}/2
                                            </span>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </>
                        ) : (
                          <div>
                            <h4 className="text-sm font-medium text-warning-400 mb-3">Partial Progress Details</h4>
                            <div className="bg-dark-700 rounded-lg p-4">
                              <p className="text-gray-300 mb-2">
                                This user started the assessment but didn't complete it. They reached:
                              </p>
                              <p className="text-warning-300 font-medium mb-3">{submission.progress}</p>
                              {submission.responses && submission.responses.raw && (
                                <div className="space-y-3">
                                  <h5 className="text-sm font-medium text-gray-400">Answered Questions:</h5>
                                  {Object.entries(submission.responses.raw).map(([category, answers]) => (
                                    <div key={category} className="bg-dark-800 p-3 rounded">
                                      <h6 className="font-medium text-white mb-2">{category}</h6>
                                      <div className="space-y-2">
                                        {Object.entries(answers).map(([index, value]) => {
                                          // Find the question text and answer option
                                          const questionData = submission.responses.questions &&
                                            submission.responses.questions[category] &&
                                            submission.responses.questions[category][parseInt(index)];

                                          return (
                                            <div key={index} className="bg-dark-600 p-2 rounded flex justify-between">
                                              <span className="text-sm text-gray-300">
                                                Question {parseInt(index) + 1}:{' '}
                                                {questionData?.question ? ` ${questionData.question}` : ''}
                                              </span>
                                              <span className="text-sm text-white">
                                                {questionData?.answer ? questionData.answer : `Score: ${value}`}
                                              </span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {(!submission.responses || Object.keys(submission.responses).length === 0) && (
                                <p className="text-gray-400 italic">No questions were answered before exiting.</p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Delete Confirmation Modal */}
                    {deleteConfirmId === submission.id && (
                      <div className="border-t border-dark-500 p-4 bg-dark-700">
                        <div className="flex items-start mb-3">
                          <div className="bg-danger-600 bg-opacity-20 p-2 rounded-full mr-3">
                            <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-danger-500" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-white">Confirm Deletion</h4>
                            <p className="text-gray-300 text-sm mt-1">
                              Are you sure you want to delete this submission? This action cannot be undone.
                            </p>
                          </div>
                        </div>

                        {deleteError && (
                          <div className="bg-danger-600 bg-opacity-20 text-danger-500 p-3 rounded-md mb-3 text-sm">
                            {deleteError}
                          </div>
                        )}

                        {deleteSuccess && (
                          <div className="bg-success-600 bg-opacity-20 text-success-500 p-3 rounded-md mb-3 text-sm">
                            Submission deleted successfully!
                          </div>
                        )}

                        <div className="flex justify-end gap-3 mt-4">
                          <button
                            onClick={cancelDelete}
                            className="px-4 py-2 bg-dark-500 text-white rounded-lg hover:bg-dark-400 transition-colors"
                            disabled={deleteLoading}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => confirmDelete(submission.id)}
                            className="px-4 py-2 bg-danger-600 text-white rounded-lg hover:bg-danger-700 transition-colors flex items-center"
                            disabled={deleteLoading}
                          >
                            {deleteLoading ? (
                              <>
                                <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2 animate-spin" />
                                Deleting...
                              </>
                            ) : (
                              <>
                                <SafeIcon icon={FiTrash2} className="w-4 h-4 mr-2" />
                                Delete
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <AnalyticsTab submissions={submissions.filter(s => !s.isPartial)} loading={loading} />
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;